/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import cookie from 'js/lib/cookie';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Typography2, breakpoints } from '@coursera/cds-core';

import * as actions from 'bundles/authentication-common/components/actions';
import { initiateLoginFlow } from 'bundles/authentication-common/components/authenticationModal/OpenAI/utils';
import UnifiedAuthEntryForm from 'bundles/authentication-common/components/forms/UnifiedAuthEntryForm';
import {
  AUTHENTICATION_FORM_VARIANTS,
  AUTHENTICATION_MODAL_MODES,
  type AUTHENTICATION_MODAL_MODES_KEY,
  type LoginMethodType,
} from 'bundles/authentication-common/constants';
import type { EnterpriseSsoLoginInfo } from 'bundles/authentication-common/hooks/useEnterpriseSsoCheck';
import type { ThirdPartyOrganizationsData } from 'bundles/authentication-common/hooks/useUserThirdPartyOrganizationsQuery';
import { loginMethodsToAuthModeWithMetadata } from 'bundles/authentication-common/utils/navigationUtils';

import _t from 'i18n!nls/authentication';

export type Props = ThirdPartyOrganizationsData & {
  className?: string;
  navigate: (
    path: AUTHENTICATION_MODAL_MODES_KEY,
    options?: Record<string, unknown>,
    shouldSwitchFormsByQueryParam?: boolean
  ) => void;
  enterpriseSsoEmailCheckInfo: {
    enterpriseSsoLoginInfo: EnterpriseSsoLoginInfo;
    checkForceSSO: (email: string) => Promise<{ isForceSSOLogin: boolean; redirectUrl: string } | null | void>;
    onContinueWithSso: () => void;
  };
  title?: string;
  tagline?: string;
  authMode?: AUTHENTICATION_MODAL_MODES_KEY;
  redirect?: string;
};

const styles = {
  container: css`
    display: grid;
    grid-template-columns: 1fr;
    margin: 0 auto;
    max-width: 396px;
    padding: 0;

    @media (min-width: 600px) {
      padding: var(--cds-spacing-50);
    }

    .cds-text-field-root {
      margin-top: var(--cds-spacing-150) !important;
    }

    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-300) var(--cds-spacing-200);
    }
  `,
  tagLine: css`
    margin: var(--cds-spacing-50) auto 0;
    width: 100%;
    padding: 0 var(--cds-spacing-400);
    text-align: center !important;
  `,
  title: css`
    margin: 0;
    padding: 0 var(--cds-spacing-600);
    margin-bottom: var(--cds-spacing-200);
    text-align: center !important;
  `,
  link: css`
    text-decoration: underline;
  `,
  organization: css`
    margin: var(--cds-spacing-200) 0 var(--cds-spacing-150) 0;
  `,
  terms: css`
    display: none;
    margin: var(--cds-spacing-150) 0 0;
    padding: 0;

    &:first-of-type {
      margin: 0;
    }

    &:nth-of-type(n) a {
      color: inherit;
      text-decoration: underline;
    }
  `,
  header: css`
    margin-bottom: var(--cds-spacing-150);
  `,
  form: css`
    form {
      gap: var(--cds-spacing-400);
    }

    .cds-input-input::placeholder {
      color: var(--cds-color-neutral-primary-weak);
    }

    .cds-input-input {
      padding: var(--cds-spacing-100);
    }

    .cds-formLabel-indicatorLabel {
      display: none;
    }
  `,
  eligibilityError: css`
    margin-top: var(--cds-spacing-200);
    font-size: var(--cds-font-size-150);
    line-height: var(--cds-line-height-200);
    padding: var(--cds-spacing-200);
    background-color: rgba(243 243 243 / 100%);
    border-radius: var(--cds-border-radius-100);

    h2 {
      font-weight: var(--cds-font-weight-600);
    }
  `,
};

const isRedirectToCapstone = (redirect: string) => {
  return redirect?.includes('capstone');
};

const OpenAIAuth = ({
  navigate,
  className,
  title,
  tagline: taglineFromProps,
  authMode,
  enterpriseSsoEmailCheckInfo,
  redirect,
  organizations,
}: Props) => {
  const { checkForceSSO } = enterpriseSsoEmailCheckInfo;

  const [submission, setSubmission] = useState<null | {
    loginMethods: Array<LoginMethodType>;
    email: string;
  }>(null);
  const [eligibilityError, setEligibilityError] = useState<boolean>(false);

  const checkEligibility = useCallback<(email: string) => Promise<boolean>>(
    async (email: string): Promise<boolean> => {
      try {
        // to support the redirect to the capstone page, we bypass the eligibility check
        if (isRedirectToCapstone(redirect ?? '')) {
          return true;
        }

        const csrfToken = cookie ? cookie.get('CSRF3-Token') : 'MISSING';
        const ri = new URL(redirect || '').searchParams.get('ri');
        const response = await fetch('/api/v1/openai/checkEligibility', {
          method: 'POST',
          body: JSON.stringify({ email, ri }),
          headers: { 'content-type': 'application/json', 'X-CSRFToken': csrfToken ?? '' },
        });

        if (!response.ok) {
          return false;
        }

        const data = await response.json();
        return data.eligible === true;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Eligibility check failed:', error);
        return false;
      }
    },
    [redirect]
  );

  useEffect(() => {
    // from authProvider query param
    const organizationId = organizations?.[0]?.id;
    if (organizationId) {
      initiateLoginFlow(organizationId, redirect);
      return;
    }

    const { isLoading, isEnterpriseSsoLogin } = enterpriseSsoEmailCheckInfo.enterpriseSsoLoginInfo;

    if (submission) {
      if (isEnterpriseSsoLogin) {
        if (isLoading) {
          return;
        }

        // from forced SSO
        enterpriseSsoEmailCheckInfo.onContinueWithSso();
        return;
      }

      const { loginMethods, email } = submission;

      // rest of the login flows
      const {
        mode,
        navigationOptions: options,
        shouldSwitchFormsByQueryParam,
      } = loginMethodsToAuthModeWithMetadata({ loginMethods, email });

      navigate(mode, options, shouldSwitchFormsByQueryParam);
    }
  }, [enterpriseSsoEmailCheckInfo, submission, navigate, organizations, redirect]);

  const handleOnError = useCallback<(email: string) => void>(
    (email: string) => {
      navigate(
        authMode === AUTHENTICATION_MODAL_MODES.SIGNUP
          ? AUTHENTICATION_MODAL_MODES.SIGNUPCONTINUE
          : AUTHENTICATION_MODAL_MODES.LOGINCONTINUE,
        { defaultEmail: email, formVariant: AUTHENTICATION_FORM_VARIANTS.PASSWORD },
        false
      );
    },
    [navigate, authMode]
  );

  const handleSubmit = useCallback<(email: string) => Promise<void>>(
    async (email: string) => {
      // Reset any previous error
      setEligibilityError(false);

      // Run the force-SSO check first so the hook's redirect state is populated for the
      // forced-SSO redirect, which the effect below performs once the state has settled.
      const forceSsoResult = await checkForceSSO(email);

      const isEligible = await checkEligibility(email);
      if (!isEligible) {
        // Set error state to prevent user from proceeding
        setEligibilityError(true);
        return;
      }

      // Force-SSO accounts skip the login-methods lookup; the effect routes them to SSO.
      if (forceSsoResult?.isForceSSOLogin) {
        setSubmission({ loginMethods: [], email });
        return;
      }

      const response = await actions.getLoginMethods(email);
      setSubmission({ loginMethods: response?.loginMethods ?? [], email });
    },
    [checkForceSSO, checkEligibility]
  );

  const tagline =
    taglineFromProps ??
    (isRedirectToCapstone(redirect ?? '')
      ? _t(
          'Use the same email you use for OpenAI Certified to access the capstone. After logging in, you’ll be taken to the capstone.'
        )
      : _t('Learn at your own pace with OpenAI Certified courses.'));

  return (
    <section css={styles.container} className={className} data-catchpoint="authentication-box-content">
      <header css={styles.header}>
        <Typography2 component="h1" variant="titleXSmall" align="left" css={styles.title}>
          {title ?? _t('Log in or create your Coursera account')}
        </Typography2>
        <Typography2 component="p" variant="bodyPrimary" color="supportText" align="left" css={styles.tagLine}>
          {tagline}
        </Typography2>
      </header>
      {eligibilityError && (
        <div css={styles.eligibilityError}>
          <Typography2 component="h2" variant="bodySecondary" color="supportText">
            {_t('Use your work email')}
          </Typography2>
          <Typography2 component="p" variant="bodySecondary" color="supportText">
            {_t(
              'We couldn’t find any OpenAI Certified learning linked to this email. Enter your work email to access your organization’s courses and resources.'
            )}
          </Typography2>
        </div>
      )}
      <div css={styles.form}>
        <UnifiedAuthEntryForm onSubmit={handleSubmit} onError={handleOnError} />
      </div>
      <div className="auth-form-tos-section">
        <Typography2 component="p" variant="bodyTertiary" color="supportText" align="left" css={styles.terms}>
          <FormattedMessage
            message={_t(
              'This site is protected by reCAPTCHA Enterprise and the Google {privacyPolicyLink} and {tosLink} apply.'
            )}
            privacyPolicyLink={
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                {_t('Privacy Policy')}
              </a>
            }
            tosLink={
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                {_t('Terms of Service')}
              </a>
            }
          />
        </Typography2>
      </div>
    </section>
  );
};

export default OpenAIAuth;
